import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedAccident } from "../../../../actions/accidentDashboard";
import {
  cleanAccidentForm,
  getAccidentFacadeAndOpen,
  getAccidentForm,
} from "../../../../actions/accidents";
import { getArtPrestadores } from "../../../../actions/arts";
import { getDocumentTypes } from "../../../../actions/documentType";
import { getFormDiagnosis, getFormEmployers } from "../../../../actions/forms";
import { changeSelectedProviderWriUser } from "../../../../actions/providers";
import snackbarUtils from "../../../../utils/snackbarUtils";
import CircularLoading from "../../../commons/loadingData/circularLoading";
import { StyledNewAccident } from "../../../styled/accident";
import AccidentFormContainer from "../../forms/accident/accidentFormContainer";
import AccidentDashboardContainer from "../accidentDashboard/accidentDashboardContainer";
import AccidentsFacadeAndOpen from "./facadeAndOpenAccidents/accidentsFacadeAndOpen";
import NewAccident from "./newAccident";

const SERENA_ART_NOMBRE = "serena art";

const PRESTADOR_INITIAL_VALUES = {
  art: null,
  tipoDocumento: "DNI",
  numeroDocumento: "",
};

const NewAccidentContainer = ({ selectProvider = false }) => {
  const dispatch = useDispatch();

  const art = useSelector((state) => state.art);
  const documentType = useSelector((state) => state.documentType.documentType);
  const artPrestadores = useSelector((state) => state.arts.artPrestadores);
  const facadeAndOpenState = useSelector(
    (state) => state.accidents.facadeAndOpen,
  );

  const [openForm, setOpenForm] = useState(false);
  const [openAccidentOpen, setOpenAccidentOpen] = useState(false);
  const [openAccidents, setOpenAccidents] = useState([]);
  const [facadeAccidents, setFacadeAccidents] = useState([]);
  const [facadeAccident, setFacadeAccident] = useState(null);
  const [artId, setArtId] = useState(null);
  const [providerId, setProviderId] = useState(null);
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [displayNewAccident, setDisplayNewAccident] = useState("block");

  const artUserArtId = artPrestadores.data?.[0]?.arts?.[0]?.id;
  const artUserMetodoCarga =
    artPrestadores.data?.[0]?.arts?.[0]?.metodoCargaSiniestros;

  const defaultPrestador = useMemo(() => {
    if (!selectProvider) {
      return null;
    }
    const list = artPrestadores.data || [];
    return (
      list.find(
        (prestador) =>
          prestador.nombre?.trim().toLowerCase() === SERENA_ART_NOMBRE,
      ) ||
      list[0] ||
      null
    );
  }, [selectProvider, artPrestadores.data]);

  const initialValues = useMemo(() => {
    if (!selectProvider) {
      return PRESTADOR_INITIAL_VALUES;
    }
    return {
      prestador: defaultPrestador,
      tipoDocumento: "DNI",
      numeroDocumento: "",
    };
  }, [selectProvider, defaultPrestador]);

  useEffect(() => {
    if (selectProvider) {
      dispatch(getArtPrestadores());
    }
    if (documentType.data.length === 0 && !documentType.isFetching) {
      dispatch(getDocumentTypes());
    }
  }, [selectProvider]);

  const handleSubmitSearch = (values) => {
    const selectedArtId = selectProvider ? artUserArtId : values.art.id;
    const metodoCarga = selectProvider
      ? artUserMetodoCarga
      : values.art.metodoCargaSiniestros;

    setArtId(selectedArtId);

    if (selectProvider) {
      setProviderId(values.prestador.id);
      dispatch(changeSelectedProviderWriUser(values.prestador.id));
    }

    switch (metodoCarga) {
      case "M":
        dispatch(
          getAccidentForm(
            selectedArtId,
            values.tipoDocumento,
            values.numeroDocumento,
            null,
            null,
          ),
        );
        dispatch(getFormEmployers(selectedArtId));
        setTipoDocumento(values.tipoDocumento);
        setNumeroDocumento(values.numeroDocumento);
        setOpenForm(true);
        break;
      case "F":
        dispatch(
          getAccidentFacadeAndOpen(
            selectedArtId,
            values.tipoDocumento,
            values.numeroDocumento,
          ),
        ).then((res) => {
          handleOpenAccidentOpen(
            res.value.data.openAccidents,
            res.value.data.facadeAccidents,
            selectProvider ? values.prestador.id : selectedArtId,
          );
        });
        break;
      default:
        snackbarUtils.error(
          "No se pueden cargar siniestros para la ART seleccionada.",
        );
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setFacadeAccident(null);
    setTipoDocumento("");
    setNumeroDocumento("");
    dispatch(cleanAccidentForm());
  };

  const handleSubmitForm = (ret) => {
    handleCloseForm();
    if (Array.isArray(ret)) {
      handleOpenAccidentOpen(ret, [], null);
    } else {
      snackbarUtils.success("Siniestro ingresado correctamente");
      handleView(ret);
    }
  };

  const handleCloseAccidentOpen = () => {
    setOpenAccidentOpen(false);
    setOpenAccidents([]);
    setFacadeAccidents([]);
  };

  const handleOpenAccidentOpen = (
    openAccidentsList,
    facadeAccidentsList,
    contextId,
  ) => {
    if (openAccidentsList.length === 0 && facadeAccidentsList.length === 1) {
      handleAddFacade(facadeAccidentsList[0], contextId);
    } else {
      setOpenAccidents(openAccidentsList);
      setFacadeAccidents(facadeAccidentsList);
      setOpenAccidentOpen(true);
    }
  };

  const handleView = (accident) => {
    dispatch(
      setSelectedAccident({
        mlCloudCoreArtId: artId,
        editable: true,
        ...accident,
      }),
    );
    handleCloseAccidentOpen();
    setDisplayNewAccident("none");
  };

  const handleAddFacade = (accident, contextId) => {
    const selectedArtId = selectProvider
      ? artUserArtId || artId
      : contextId || artId;
    if (selectProvider) {
      setProviderId(contextId || providerId);
    } else {
      setArtId(selectedArtId);
    }
    setFacadeAccident(accident);
    dispatch(
      getAccidentForm(
        selectedArtId,
        null,
        null,
        null,
        accident.diagnosticoOMS,
      ),
    );
    dispatch(getFormEmployers(selectedArtId));
    dispatch(getFormDiagnosis(selectedArtId));
    setOpenForm(true);
    handleCloseAccidentOpen();
  };

  const isLoading = selectProvider
    ? artPrestadores.isFetching ||
      documentType.isFetching ||
      facadeAndOpenState.isFetching
    : art.isFetching ||
      documentType.isFetching ||
      facadeAndOpenState.isFetching;

  return (
    <div>
      {displayNewAccident === "block" ? (
        isLoading ? (
          <StyledNewAccident.Loading>
            <CircularLoading />
          </StyledNewAccident.Loading>
        ) : (
          <NewAccident
            handleSubmit={handleSubmitSearch}
            documentTypes={documentType.data}
            firstFieldName={selectProvider ? "prestador" : "art"}
            firstFieldLabel={selectProvider ? "Prestador" : "Art"}
            firstFieldOptions={
              selectProvider ? artPrestadores.data || [] : art.artsAdhered
            }
            initialValues={initialValues}
          />
        )
      ) : (
        <AccidentDashboardContainer
          setDisplayAccidents={setDisplayNewAccident}
        />
      )}
      <AccidentFormContainer
        open={openForm}
        artId={artId}
        providerId={providerId}
        handleCloseForm={handleCloseForm}
        handleSubmitForm={handleSubmitForm}
        facadeAccident={facadeAccident}
        tipoDocumento={tipoDocumento}
        numeroDocumento={numeroDocumento}
      />
      <AccidentsFacadeAndOpen
        open={openAccidentOpen}
        handleClose={handleCloseAccidentOpen}
        openAccidents={openAccidents}
        facadeAccidents={facadeAccidents}
        handleView={handleView}
        handleAdd={handleAddFacade}
      />
    </div>
  );
};

export default NewAccidentContainer;
